// 💰 Animated Muse Cash Counter Component
import React, { useState, useEffect, useRef } from 'react';

interface AnimatedMuseCashProps {
  value: number;
  lastReward?: number; // Amount that was just added
  onAnimationComplete?: () => void;
}

const AnimatedMuseCash: React.FC<AnimatedMuseCashProps> = ({ 
  value, 
  lastReward,
  onAnimationComplete 
}) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const prevValueRef = useRef(value);
  const animationRef = useRef<number | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Audio context for sound effects
  const audioContextRef = useRef<AudioContext | null>(null);
  
  const getAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  };

  const playCashSound = () => {
    try {
      const audioContext = getAudioContext();
      
      // Create a pleasant, bell-like chime sound
      const oscillator1 = audioContext.createOscillator();
      const oscillator2 = audioContext.createOscillator();
      const oscillator3 = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      const filter = audioContext.createBiquadFilter();
      
      // Use harmonious frequencies (major chord: C5, E5, G5)
      oscillator1.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
      oscillator2.frequency.setValueAtTime(659.25, audioContext.currentTime); // E5
      oscillator3.frequency.setValueAtTime(783.99, audioContext.currentTime); // G5
      
      // Gentle frequency shimmer
      oscillator1.frequency.exponentialRampToValueAtTime(520, audioContext.currentTime + 0.4);
      oscillator2.frequency.exponentialRampToValueAtTime(655, audioContext.currentTime + 0.4);
      oscillator3.frequency.exponentialRampToValueAtTime(780, audioContext.currentTime + 0.4);
      
      // Use sine waves for pure, pleasant tones
      oscillator1.type = 'sine';
      oscillator2.type = 'sine';
      oscillator3.type = 'sine';
      
      // Add a low-pass filter for warmth
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(2000, audioContext.currentTime);
      filter.Q.setValueAtTime(1, audioContext.currentTime);
      
      // Gentle bell-like envelope
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.03, audioContext.currentTime + 0.02);
      gainNode.gain.linearRampToValueAtTime(0.025, audioContext.currentTime + 0.1);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.6);
      
      // Connect the audio graph
      oscillator1.connect(filter);
      oscillator2.connect(filter);
      oscillator3.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      const now = audioContext.currentTime;
      oscillator1.start(now);
      oscillator2.start(now);
      oscillator3.start(now);
      oscillator1.stop(now + 0.6);
      oscillator2.stop(now + 0.6);
      oscillator3.stop(now + 0.6);
    } catch (error) {
      console.log('Audio not available:', error);
    }
  };

  useEffect(() => {
    // On first mount, just set the initial value without animation
    if (!isInitialized) {
      setDisplayValue(value);
      setIsInitialized(true);
      prevValueRef.current = value;
      return;
    }

    const prevValue = prevValueRef.current;
    const difference = value - prevValue;

    if (difference !== 0) {
      // Start animation for both increases and decreases
      setIsAnimating(true);
      if (difference > 0) setShowReward(true);

      const duration = 1200; // ms - slightly faster for better responsiveness
      const startTime = Date.now();
      const startValue = prevValue;
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 2);
        const currentValue = Math.round(startValue + (difference * easeOut));
        setDisplayValue(currentValue);

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
          onAnimationComplete?.();
        }
      };
      animationRef.current = requestAnimationFrame(animate);

      if (difference > 0) {
        setTimeout(() => setShowReward(false), 1500);
      }
    } else {
      setDisplayValue(value);
    }

    prevValueRef.current = value;

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value, onAnimationComplete, isInitialized]);

  const handleClick = () => {
    setIsClicked(true);
    playCashSound();
    // Reset click animation after it completes
    setTimeout(() => setIsClicked(false), 600);
  };

  return (
    <div
      style={{ position: 'relative', display: 'inline-block', cursor: 'pointer', overflow: 'visible' }}
      onClick={handleClick}
    >
      {/* Main Muse Cash Display */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          fontWeight: 800,
          fontSize: '1.4rem',
          color: (isAnimating || isClicked) ? '#FFD700' : '#fff',
          transition: 'all 0.3s ease',
          transform: isAnimating
            ? 'scale(1.1)'
            : isClicked
            ? 'scale(1.15) rotate(5deg)'
            : 'scale(1)',
          textShadow: isAnimating
            ? '0 0 10px rgba(255, 215, 0, 0.8), 0 0 20px rgba(255, 215, 0, 0.4), 0 0 5px rgba(205, 133, 63, 0.8)'
            : isClicked
            ? '0 0 15px rgba(255, 215, 0, 0.9), 0 0 25px rgba(255, 215, 0, 0.5), 0 0 8px rgba(205, 133, 63, 0.9)'
            : '0 0 5px rgba(205, 133, 63, 0.6), 0 0 10px rgba(205, 133, 63, 0.3)'
        }}
      >
        <span 
          role="img" 
          aria-label="Muse Cash" 
          style={{
            marginRight: 8,
            fontSize: '1.8rem'
          }}
        >
          💰
        </span>
        <span
          style={{
            fontVariantNumeric: 'tabular-nums',
            animation: isAnimating
              ? 'numberPulse 0.8s ease-out'
              : isClicked
              ? 'clickNumberShake 0.6s ease-out'
              : undefined,
            display: 'inline-block',
            minWidth: 'auto',
            lineHeight: '1',
            verticalAlign: 'baseline'
          }}
        >
          {displayValue.toLocaleString()}
        </span>
      </div>

      {/* Floating +Cash Animation */}
      {showReward && lastReward && lastReward > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '-10px',
            right: '-10px',
            background: 'linear-gradient(135deg, #2ECC71 0%, #27AE60 100%)',
            color: 'white',
            borderRadius: '20px',
            padding: '6px 12px',
            fontSize: '14px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(46, 204, 113, 0.4)',
            animation: 'floatUp 1.5s ease-out forwards',
            zIndex: 1000,
            border: '2px solid rgba(255, 255, 255, 0.3)',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none'
          }}
        >
          +{lastReward}
        </div>
      )}

      {/* Sparkle Effects */}
      {(isAnimating || isClicked) && (
        <>
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: `${20 + (i * 10)}%`,
                left: `${10 + (i * 15)}%`,
                width: '4px',
                height: '4px',
                background: '#FFD700',
                borderRadius: '50%',
                animation: isClicked
                  ? `clickSparkle${i % 3} 0.6s ease-out ${i * 0.05}s`
                  : `sparkle${i % 3} 0.8s ease-out ${i * 0.1}s`,
                zIndex: 999,
                pointerEvents: 'none'
              }}
            />
          ))}
        </>
      )}

      {/* Click Effect - Coin Rain (positioned away from center) */}
      {isClicked && (
        <>
          {[...Array(6)].map((_, i) => (
            <div
              key={`coin-${i}`}
              style={{
                position: 'absolute',
                top: i % 2 === 0 ? '20%' : '80%',
                left: i % 3 === 0 ? '10%' : i % 3 === 1 ? '90%' : '50%',
                fontSize: '0.8rem',
                animation: `coinRain${i % 4} 0.8s ease-out ${i * 0.05}s`,
                zIndex: 1000,
                pointerEvents: 'none'
              }}
            >
              💰
            </div>
          ))}
        </>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes coinSpin {
          0% { transform: rotateY(0deg); }
          50% { transform: rotateY(180deg) scale(1.2); }
          100% { transform: rotateY(360deg); }
        }

        @keyframes numberPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }

        @keyframes floatUp {
          0% {
            transform: translateY(0px) scale(0.8);
            opacity: 0;
          }
          20% {
            transform: translateY(-10px) scale(1.1);
            opacity: 1;
          }
          100% {
            transform: translateY(-40px) scale(0.9);
            opacity: 0;
          }
        }

        @keyframes sparkle0 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(1) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes sparkle1 {
          0% {
            transform: scale(0) translateX(0px);
            opacity: 1;
          }
          50% {
            transform: scale(1.5) translateX(10px);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateX(20px);
            opacity: 0;
          }
        }

        @keyframes sparkle2 {
          0% {
            transform: scale(0) translateY(0px);
            opacity: 1;
          }
          50% {
            transform: scale(1.2) translateY(-15px);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateY(-30px);
            opacity: 0;
          }
        }

        /* Click Animation Keyframes */
        @keyframes clickCoinBounce {
          0% { transform: scale(1) rotateY(0deg); }
          25% { transform: scale(1.3) rotateY(90deg); }
          50% { transform: scale(1.5) rotateY(180deg); }
          75% { transform: scale(1.2) rotateY(270deg); }
          100% { transform: scale(1) rotateY(360deg); }
        }

        @keyframes clickNumberShake {
          0%, 100% { transform: translateX(0); }
          10% { transform: translateX(-2px) scale(1.05); }
          20% { transform: translateX(2px) scale(1.1); }
          30% { transform: translateX(-2px) scale(1.05); }
          40% { transform: translateX(2px) scale(1.1); }
          50% { transform: translateX(-1px) scale(1.05); }
          60% { transform: translateX(1px) scale(1.1); }
          70% { transform: translateX(-1px) scale(1.05); }
          80% { transform: translateX(1px) scale(1.1); }
          90% { transform: translateX(0) scale(1.05); }
        }

        @keyframes clickSparkle0 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(1.5) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes clickSparkle1 {
          0% {
            transform: scale(0) translateX(0px) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(2) translateX(15px) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateX(30px) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes clickSparkle2 {
          0% {
            transform: scale(0) translateY(0px) rotate(0deg);
            opacity: 1;
          }
          50% {
            transform: scale(1.8) translateY(-20px) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: scale(0) translateY(-40px) rotate(360deg);
            opacity: 0;
          }
        }

        @keyframes coinRain0 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          25% {
            transform: translate(-30px, -20px) scale(1.2) rotate(180deg);
            opacity: 1;
          }
          100% {
            transform: translate(-60px, 40px) scale(0.3) rotate(720deg);
            opacity: 0;
          }
        }

        @keyframes coinRain1 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          25% {
            transform: translate(30px, -20px) scale(1.2) rotate(-180deg);
            opacity: 1;
          }
          100% {
            transform: translate(60px, 40px) scale(0.3) rotate(-720deg);
            opacity: 0;
          }
        }

        @keyframes coinRain2 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          25% {
            transform: translate(-20px, -30px) scale(1.2) rotate(270deg);
            opacity: 1;
          }
          100% {
            transform: translate(-40px, 60px) scale(0.3) rotate(1080deg);
            opacity: 0;
          }
        }

        @keyframes coinRain3 {
          0% {
            transform: scale(0) rotate(0deg);
            opacity: 1;
          }
          25% {
            transform: translate(20px, -30px) scale(1.2) rotate(-270deg);
            opacity: 1;
          }
          100% {
            transform: translate(40px, 60px) scale(0.3) rotate(-1080deg);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default AnimatedMuseCash; 